import { logout } from '../api/UserApi';
import makeRequest from './Request';

interface Request {
    body?: { [key: string]: any };
    method?: string;
    headers?: { [key: string]: any };
    token?: string;
}

function addTrailingSlash(endpoint: string) {
    if (endpoint.includes('?')) {
        return endpoint;
    } else if (endpoint.charAt(endpoint.length - 1) !== '/') {
        return `${endpoint}${'/'}`;
    }
    return endpoint;
}

type NetworkSuccess = {
    ok: true;
    response: Response;
};

type RequestFailureError = {
    ok: false;
    message: string;
    error: unknown;
};

type HttpResponseError = {
    ok: false;
    message: string;
    response: Response;
};

type NetworkError = RequestFailureError | HttpResponseError;
type NetworkResult = NetworkSuccess | NetworkError;

type ClientOptions = {
    stringify?: boolean;
};

async function rawResponseClient(
    endpoint: string,
    { body, ...customConfig }: Request = {},
    options: ClientOptions = {},
): Promise<NetworkResult> {
    const { stringify = true } = options;

    const updatedEndpoint = addTrailingSlash(endpoint);
    let response: Response;

    try {
        response = await makeRequest(updatedEndpoint, { body, ...customConfig }, stringify);
    } catch (e) {
        return { ok: false, message: 'Error', error: e };
    }

    if (response.status === 401) {
        logout();
        return { ok: false, message: 'Unauthorized', response };
    }

    if (!response.ok) {
        return { ok: false, message: 'Bad response', response };
    }

    return { ok: true, response };
}

async function client(endpoint: string, { body, ...customConfig }: Request = {}, stringify: boolean = true) {
    const updatedEndpoint = addTrailingSlash(endpoint);
    const response = await makeRequest(updatedEndpoint, { body, ...customConfig }, stringify);
    const text = await response.text();

    const data = text ? JSON.parse(text) : null;
    if (response.ok) {
        return data;
    } else if (response.status === 401) {
        logout();
    } else {
        if (data && data.errorCode === 'HAL9000' && window.location.href.indexOf('out-of-country') < 0) {
            window.location.href = '/out-of-country';
        }
        return Promise.reject(data);
    }
}

function extractErrorMessage(error: unknown, fallback: string): string {
    if (!error || typeof error !== 'object') return fallback;
    const data = error as Record<string, unknown>;
    if (typeof data.detail === 'string') return data.detail;
    for (const value of Object.values(data)) {
        if (typeof value === 'string') return value;
        if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
    return fallback;
}

export default client;
export { rawResponseClient, extractErrorMessage };
